// services/payroll.service.js

const API_URL = "https://ims.holdthechild.net";

/**
 * Get JWT token from localStorage
 */
export const getToken = () => localStorage.getItem("token");

/**
 * Generic GET request helper
 */
const getRequest = async (endpoint, params = {}) => {
  try {
    const token = getToken();
    const query = new URLSearchParams(params).toString();
    // Append timestamp to bypass cache/304
    const url = `${API_URL}${endpoint}${query ? `?${query}&_t=${Date.now()}` : `?_t=${Date.now()}`}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      cache: "no-store", // ensure fresh data
    });

    // Handle 304 Not Modified safely
    if (response.status === 304) {
      console.warn(`${endpoint} returned 304 Not Modified`);
      return []; // fallback empty array
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: "Server error" }));
      throw new Error(errorBody.message || "Request failed");
    }

    return await response.json();
  } catch (error) {
    console.error(`GET ${endpoint} error:`, error.message);
    throw error;
  }
};

/**
 * Generic POST request helper
 */
const postRequest = async (endpoint, body = {}) => {
  try {
    const token = getToken();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: "Server error" }));
      throw new Error(errorBody.message || "Request failed");
    }

    return await response.json();
  } catch (error) {
    console.error(`POST ${endpoint} error:`, error.message);
    throw error;
  }
};

/**
 * Generic PATCH request helper
 */
const patchRequest = async (endpoint, body = {}) => {
  try {
    const token = getToken();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: "Server error" }));
      throw new Error(errorBody.message || "Request failed");
    }

    return await response.json();
  } catch (error) {
    console.error(`PATCH ${endpoint} error:`, error.message);
    throw error;
  }
};

/* ================= Payroll Services ================= */

/**
 * Fetch payroll list (supports optional pagination/search)
 */
export const fetchPayrollList = async (page = 1, limit = 50, search = "") => {
  const res = await getRequest("/api/payrolls", { page, limit, search });
  if (Array.isArray(res)) return res;
  if (res.data && Array.isArray(res.data)) return res.data;
  console.warn("Unexpected payroll list format:", res);
  return [];
};

/**
 * Fetch payroll statistics
 */
export const fetchPayrollStats = async () => {
  const res = await getRequest("/api/payrolls/stats");
  return res || { totalRecords: 0, paid: 0, unpaid: 0, totalNetSalary: 0 };
};

/**
 * Fetch individual payroll/payslip
 */
export const fetchPayslip = async (id) => {
  return getRequest(`/api/payrolls/${id}`);
};

/**
 * Fetch staff list for payroll modal
 */
export const fetchStaffList = async () => {
  const res = await getRequest("/api/staff");
  // Normalize response to always return array
  if (Array.isArray(res)) return res;
  if (res.staff && Array.isArray(res.staff)) return res.staff;
  console.warn("Unexpected staff list format:", res);
  return [];
};

/**
 * Create a new payroll record
 */
export const createPayroll = async (payload) => {
  return postRequest("/api/payrolls", payload);
};

/* ================= Approval Workflow Services ================= */

/**
 * Mark payroll as prepared
 */
export const preparePayroll = async (id) => {
  return patchRequest(`/api/payrolls/${id}/prepare`, {});
};

/**
 * Mark payroll as checked
 */
export const checkPayroll = async (id) => {
  return patchRequest(`/api/payrolls/${id}/check`, {});
};

/**
 * Approve a payroll record
 * FIX: Renamed 'comments' argument to 'payload' and passed it directly 
 * to prevent double wrapping the object.
 */
export const approvePayroll = async (id, payload) => {
  return patchRequest(`/api/payrolls/${id}/approve`, payload);
};

/**
 * Reject a payroll record
 */
export const rejectPayroll = async (id, payload) => {
  // Payload expects { reason: "some string" }
  return patchRequest(`/api/payrolls/${id}/reject`, payload);
};



/* ================= Payment Services ================= */

/**
 * Mark payroll as paid
 */
export const markPayrollPaid = async (id) => {
  return patchRequest(`/api/payrolls/${id}/pay`);
};

/**
 * Mass pay multiple payroll records
 */
export const markPayrollMassPaid = async (ids = []) => {
  return patchRequest("/api/payrolls/mass/pay", { payrollIds: ids });
};